import 'isomorphic-fetch';
import {
  getInvalidStates,
  getSuccessStates,
  FormResponseState,
} from './helpers/response-states';
import { stringMap } from '../../typings/general';

interface RequestOptions {
  readonly faServicesUrl: string;
  readonly mockIds?: string;
  readonly mocksBaseUrl?: string;
  readonly userParams: stringMap;
  readonly requestType: string;
  readonly paramsType: string;
}

function createSubmitUrl(
  params: URLSearchParams,
  urlOrigin: string,
  isHandraiser = false,
): string {
  return `${urlOrigin}/${
    isHandraiser ? 'handraiser' : 's2f'
  }?${params.toString()}`;
}

export class SubmitFromError extends Error {
  public constructor(message: string, public readonly url?: string) {
    super(message);
  }
}

function buildHandraiserPayload(userParams?: stringMap) {
  const params = userParams ?? {};

  const survey: {
    questionCode: string;
    answerCode: string;
    answer: string;
  }[] = [];

  // Build survey array dynamically
  for (let i = 1; ; i++) {
    const qKey = `surveyQ${i}`;
    const aKey = `surveyA${i}`;

    if (!(qKey in params) || !(aKey in params)) break;

    const [questionCode, answerCode] = (params[qKey] || '').split('/');
    const answer = params[aKey];

    if (questionCode && answerCode && answer) {
      survey.push({ questionCode, answerCode, answer });
    }
  }

  return {
    originCountry: 'US',
    brand: params.brand,
    customer: {
      firstName: params.firstName,
      lastName: params.lastName,
      contact: { email: params.email },
      address: { zip: params.zip },
    },
    campaign: { id: params.campaignID },
    vehicles: params.vehicles ?? [],
    additionalNote: [{ text: params.comments ?? '' }],
    surveyInfo: {
      survey,
    },
  };
}

export async function submitForm({
  faServicesUrl,
  mockIds,
  mocksBaseUrl,
  userParams,
  requestType,
  paramsType,
}: RequestOptions): Promise<FormResponseState> {
  const params = new URLSearchParams();

  if (mockIds) {
    params.set('mockIds', mockIds);
  }

  if (mocksBaseUrl) {
    params.set('mocksBaseUrl', mocksBaseUrl);
  }

  if (requestType === 'handraiser') {
    const dataToSend = buildHandraiserPayload(userParams);
    params.set('queryParamsJson', JSON.stringify(dataToSend));
  } else {
    if (paramsType === 'json') {
      params.set('json', JSON.stringify(userParams));
    } else {
      params.set('queryParamsJson', JSON.stringify(userParams));
    }
  }

  params.set('s2fFormPath', requestType);

  const response = await fetch(
    createSubmitUrl(params, faServicesUrl, requestType === 'handraiser'),
  );
  const result = await response.json();
  if (requestType !== 'handraiser' && (!result || !result.leadId)) {
    console.warn(`Form submit failed, did not get a lead ID`);
    return {
      data: {},
      ...getInvalidStates(),
      submitted: true,
    };
  } else if (
    requestType === 'handraiser' &&
    (!result || !result.handraiserId)
  ) {
    console.warn(`Form submit failed, did not get a handraiser ID`);
    return {
      data: {},
      ...getInvalidStates(),
      submitted: true,
    };
  }
  return {
    data: result,
    ...getSuccessStates(),
    submitted: true,
  };
}
